import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, FileText, Pencil, Trash2, AlertCircle, CalendarDays, ShieldCheck,
  AlertTriangle, CheckCircle2,
} from "lucide-react";

const STATUS_STYLES = {
  valid: { wrapper: "bg-[#DCFCE7] text-[#15803D]", icon: CheckCircle2, label: "Valid" },
  expiring_soon: { wrapper: "bg-[#FEF3C7] text-[#B45309]", icon: AlertTriangle, label: "Expiring soon" },
  expired: { wrapper: "bg-[#FEE2E2] text-[#B91C1C]", icon: AlertCircle, label: "Expired" },
};

function StatusPill({ status, days }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.valid;
  const Icon = cfg.icon;
  let text = cfg.label;
  if (status === "expiring_soon") text = `Expires in ${days} day${days === 1 ? "" : "s"}`;
  if (status === "expired") text = `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.wrapper}`} data-testid={`doc-status-${status}`}>
      <Icon className="w-3.5 h-3.5" />
      {text}
    </span>
  );
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function BranchDocumentsPage() {
  const { selectedBranch, allBranches, setSelectedBranchId, setSelectedSchoolId } = useBranch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const perms = user?.permissions || {};
  const canManage = isOwner || !!perms.branch_documents_manage;

  // Honour ?branch=<id> deep-links from the dashboard widget by switching the
  // BranchContext to that branch (and its parent school) on mount.
  useEffect(() => {
    const target = searchParams.get("branch");
    if (!target) return;
    const match = (allBranches || []).find((b) => b.id === target);
    if (!match) return;
    if (selectedBranch?.id === target) {
      const next = new URLSearchParams(searchParams);
      next.delete("branch");
      setSearchParams(next, { replace: true });
      return;
    }
    if (match.school_id) setSelectedSchoolId(match.school_id);
    setSelectedBranchId(target);
  }, [searchParams, allBranches, selectedBranch?.id, setSelectedBranchId, setSelectedSchoolId, setSearchParams]);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ title: "", issue_date: todayIso(), expiration_date: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const branchId = selectedBranch?.id;

  const load = useCallback(async () => {
    if (!branchId) { setDocuments([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/branches/${branchId}/documents`);
      setDocuments(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setDocuments([]);
    } finally { setLoading(false); }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setEditing(null);
    setForm({ title: "", issue_date: todayIso(), expiration_date: "", notes: "" });
    setDialogOpen(true);
  }

  function openEdit(doc) {
    setEditing(doc);
    setForm({
      title: doc.title || "",
      issue_date: doc.issue_date || todayIso(),
      expiration_date: doc.expiration_date || "",
      notes: doc.notes || "",
    });
    setDialogOpen(true);
  }

  async function submitForm(e) {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.issue_date || !form.expiration_date) { toast.error("Issue and expiration dates are required"); return; }
    if (form.expiration_date < form.issue_date) { toast.error("Expiration date must be on or after issue date"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/branch-documents/${editing.id}`, form);
        toast.success("Document updated");
      } else {
        await api.post(`/branches/${branchId}/documents`, form);
        toast.success("Document added");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  }

  async function confirmDeleteDoc() {
    if (!confirmDelete) return;
    try {
      await api.delete(`/branch-documents/${confirmDelete.id}`);
      toast.success("Document removed");
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  if (!selectedBranch) {
    return (
      <div className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-floating p-10 text-center max-w-xl" data-testid="no-branch-picked">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
          <AlertCircle className="w-7 h-7 text-[#E65100]" />
        </div>
        <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
        <p className="text-slate-500 mt-1 text-sm">Documents are scoped per branch — pick a branch from the top bar to manage its permits, licenses, and accreditation records.</p>
      </div>
    );
  }

  const expiringCount = documents.filter((d) => d.status === "expiring_soon").length;
  const expiredCount = documents.filter((d) => d.status === "expired").length;
  const validCount = documents.filter((d) => d.status === "valid").length;

  return (
    <div className="space-y-6" data-testid="branch-documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground tracking-tight">Documents</h1>
          <p className="text-slate-500 mt-1">
            Permits, licenses & accreditation records for <span className="font-semibold text-[#1D4ED8]">{selectedBranch.name}</span>. We warn you 14 days before any expiration.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={openAdd}
            className="rounded-full px-5 h-11 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white shadow-floating"
            data-testid="add-document-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add document
          </Button>
        )}
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile color="green" Icon={CheckCircle2} label="Valid" value={validCount} testid="stat-valid" />
        <StatTile color="amber" Icon={AlertTriangle} label="Expiring soon (≤14 days)" value={expiringCount} testid="stat-expiring" />
        <StatTile color="red" Icon={AlertCircle} label="Expired" value={expiredCount} testid="stat-expired" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-floating overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : documents.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-documents">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#EFF6FF] flex items-center justify-center mb-3">
              <ShieldCheck className="w-8 h-8 text-[#2563EB]" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">No documents tracked yet</p>
            <p className="text-slate-500 mt-1 max-w-md mx-auto">
              {canManage
                ? "Click \"Add document\" to log a permit, license or accreditation (e.g. Mayor's Permit, BIR Certificate, DepEd Accreditation, Fire Safety Inspection)."
                : "Ask the school owner to add documents for this branch."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="documents-table">
              <thead>
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Document</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Issue Date</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Expiration</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  {canManage && <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {documents.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-foreground flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#2563EB]" />
                        {d.title}
                      </div>
                      {d.notes && <div className="text-xs text-slate-500 mt-0.5 max-w-md truncate">{d.notes}</div>}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                        {d.issue_date}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                        {d.expiration_date}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <StatusPill status={d.status} days={d.days_until_expiration} />
                    </td>
                    {canManage && (
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm" variant="ghost"
                            className="h-8 w-8 p-0 text-slate-500 hover:text-[#1D4ED8] hover:bg-[#EFF6FF]"
                            onClick={() => openEdit(d)}
                            data-testid={`doc-edit-${d.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="h-8 w-8 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => setConfirmDelete(d)}
                            data-testid={`doc-delete-${d.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="document-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit document" : "Add document"}</DialogTitle>
            <DialogDescription>
              Track issue + expiration dates so we can warn you before it lapses.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-4" data-testid="document-form">
            <div>
              <Label htmlFor="doc-title">Title <span className="text-red-500">*</span></Label>
              <Input
                id="doc-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Mayor's Permit"
                maxLength={200}
                data-testid="document-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="doc-issue">Issue date <span className="text-red-500">*</span></Label>
                <Input
                  id="doc-issue"
                  type="date"
                  value={form.issue_date}
                  onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                  data-testid="document-issue-input"
                />
              </div>
              <div>
                <Label htmlFor="doc-expiration">Expiration <span className="text-red-500">*</span></Label>
                <Input
                  id="doc-expiration"
                  type="date"
                  value={form.expiration_date}
                  onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
                  data-testid="document-expiration-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="doc-notes">Notes</Label>
              <Textarea
                id="doc-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional reference numbers, issuing agency, renewal instructions…"
                rows={3}
                maxLength={1000}
                data-testid="document-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="document-cancel-btn">Cancel</Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white"
                data-testid="document-save-btn"
              >
                {saving ? "Saving…" : (editing ? "Save changes" : "Add document")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="document-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-semibold">{confirmDelete?.title}</span>. You can re-add it later, but historical reminders will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="document-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteDoc}
              className="bg-red-600 hover:bg-red-700"
              data-testid="document-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatTile({ color, Icon, label, value, testid }) {
  const palette = {
    green: { bg: "bg-[#DCFCE7]", text: "text-[#15803D]", border: "border-green-100" },
    amber: { bg: "bg-[#FEF3C7]", text: "text-[#B45309]", border: "border-amber-100" },
    red:   { bg: "bg-[#FEE2E2]", text: "text-[#B91C1C]", border: "border-red-100" },
  }[color];
  return (
    <div className={`bg-white rounded-2xl border ${palette.border} shadow-floating p-4 flex items-center gap-3`} data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl ${palette.bg} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${palette.text}`} />
      </div>
      <div>
        <div className="text-2xl font-bold text-foreground">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}
